import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { FiSearch, FiCheckCircle, FiAlertTriangle, FiExternalLink, FiHeart, FiShield, FiPhone, FiMail, FiDollarSign, FiClock } from 'react-icons/fi'
import { useCharitySearch, type CharityResult } from '@/hooks/useCharityChecker'
import Breadcrumbs from '@/components/Breadcrumbs'
import { getUserFromClerkId } from '@/hooks/useUser'
import { useUser } from '@clerk/clerk-react'

export const Route = createFileRoute('/_auth/tools/charity-checker')({
  validateSearch: (search: Record<string, unknown>) => ({
    q: typeof search.q === 'string' ? search.q : undefined,
    invitationcode: typeof search.invitationcode === 'string' ? search.invitationcode : undefined,
    __clerk_ticket: typeof search.__clerk_ticket === 'string' ? search.__clerk_ticket : undefined,
  }),
  component: CharityCheckerPage,
})

function formatCurrency(amount: number, country: string): string {
  const symbol = country === 'US' ? '$' : '£'
  if (amount >= 1_000_000) return `${symbol}${(amount / 1_000_000).toFixed(1)}M`
  if (amount >= 1_000) return `${symbol}${(amount / 1_000).toFixed(0)}K`
  return `${symbol}${amount.toLocaleString()}`
}

function StatusBadge({ status }: { status: CharityResult['status'] }) {
  if (status === 'registered') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-700">
        <FiCheckCircle className="h-3.5 w-3.5" />
        Registered
      </span>
    )
  }
  if (status === 'removed') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-sm font-medium text-red-700">
        <FiAlertTriangle className="h-3.5 w-3.5" />
        Removed
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-600">
      Unknown
    </span>
  )
}

function ResultCard({ charity }: { charity: CharityResult }) {
  return (
    <div className="rounded-2xl bg-white p-5 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)]">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-slate-900">{charity.name}</h3>
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            <StatusBadge status={charity.status} />
            {charity.registrationNumber && (
              <span className="text-sm text-slate-400">#{charity.registrationNumber}</span>
            )}
            {charity.city && charity.state && (
              <span className="text-sm text-slate-400">{charity.city}, {charity.state}</span>
            )}
          </div>
        </div>
        <a
          href={charity.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-slate-200/60 px-3 py-1.5 text-sm font-medium text-slate-500 transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700"
        >
          View on register
          <FiExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>

      {/* Details row */}
      <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-500">
        {charity.yearsActive != null && (
          <div className="flex items-center gap-1.5">
            <FiClock className="h-4 w-4 text-slate-400" />
            <span>{charity.yearsActive} {charity.yearsActive === 1 ? 'year' : 'years'} active</span>
          </div>
        )}
        {charity.income != null && (
          <div className="flex items-center gap-1.5">
            <FiDollarSign className="h-4 w-4 text-slate-400" />
            <span>Income: {formatCurrency(charity.income, charity.country)}</span>
          </div>
        )}
        {charity.assets != null && (
          <div className="flex items-center gap-1.5">
            <FiShield className="h-4 w-4 text-slate-400" />
            <span>Assets: {formatCurrency(charity.assets, charity.country)}</span>
          </div>
        )}
        {charity.classification && (
          <span className="text-slate-400">{charity.classification}</span>
        )}
      </div>

      {/* Trust signals / warnings */}
      {charity.status === 'registered' && charity.yearsActive != null && (
        <div className="mt-4">
          {charity.yearsActive < 2 ? (
            <div className="rounded-xl bg-amber-50 px-4 py-3">
              <div className="flex items-start gap-2">
                <FiAlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <p className="text-sm text-amber-800">
                  This charity was registered recently (less than 2 years ago). While it may be completely legitimate, newer charities have less of a track record. Consider verifying them further before donating large amounts.
                </p>
              </div>
            </div>
          ) : charity.yearsActive >= 10 ? (
            <div className="rounded-xl bg-emerald-50 px-4 py-3">
              <div className="flex items-start gap-2">
                <FiCheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                <p className="text-sm text-emerald-800">
                  This charity has been registered for over {charity.yearsActive} years — a good indicator of legitimacy and stability.
                </p>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {charity.status === 'removed' && (
        <div className="mt-4 rounded-xl bg-red-50 px-4 py-3">
          <div className="flex items-start gap-2">
            <FiAlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
            <p className="text-sm text-red-800">
              This charity has been removed from the register. This could mean it was dissolved, merged with another charity, or had its status revoked. <strong>Do not donate</strong> until you have verified its current status directly.
            </p>
          </div>
        </div>
      )}

      <p className="mt-3 text-xs text-slate-400">Source: {charity.source}</p>
    </div>
  )
}

function CharityCheckerPage() {
  const { user: clerkUser } = useUser()
  const userQuery = getUserFromClerkId(clerkUser?.id || '', { enabled: !!clerkUser?.id })
  const userMarket = userQuery.data?.market || 'GB'
  const searchParams = Route.useSearch()
  const initialQuery = searchParams.q || ''
  const [query, setQuery] = useState(initialQuery)
  const [searchTerm, setSearchTerm] = useState(initialQuery)
  const charitySearch = useCharitySearch(searchTerm)

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (query.trim().length >= 2) {
      setSearchTerm(query.trim())
    }
  }

  const registerName = userMarket === 'US' ? 'IRS' : 'Charity Commission'
  const exampleSearch = userMarket === 'US' ? 'American Red Cross' : 'British Red Cross'

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <div className="mx-auto max-w-4xl px-6 py-8 sm:px-8 sm:py-10">

        <Breadcrumbs
          items={[
            { label: 'Tools', href: '/tools' },
            { label: 'Charity Checker' },
          ]}
        />

        {/* Hero */}
        <header className="mt-4 mb-10">
          <div className="flex items-center gap-3.5 mb-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-[15px] bg-rose-50 text-rose-600">
              <FiHeart className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
                Charity Checker
              </h1>
              <p className="mt-0.5 text-base text-slate-500">
                Verify a charity is legitimate before you donate.
              </p>
            </div>
          </div>
        </header>

        {/* Search */}
        <div className="rounded-2xl bg-white p-6 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] sm:p-8 mb-8">
          <form onSubmit={handleSearch}>
            <label htmlFor="charity-search" className="block text-base font-medium text-slate-700 mb-2">
              Search by charity name or registration number
            </label>
            <div className="flex gap-3">
              <div className="relative flex-1">
                <FiSearch className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <input
                  id="charity-search"
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={`e.g., "${exampleSearch}" or a registration number`}
                  className="w-full rounded-xl border border-slate-200 bg-white py-3.5 pl-12 pr-4 text-base text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
                />
              </div>
              <button
                type="submit"
                disabled={query.trim().length < 2 || charitySearch.isLoading}
                className="shrink-0 rounded-xl bg-violet-600 px-6 py-3.5 text-base font-medium text-white transition-colors hover:bg-violet-700 disabled:opacity-30 cursor-pointer"
              >
                {charitySearch.isLoading ? 'Searching...' : 'Check'}
              </button>
            </div>
            <p className="mt-2 text-sm text-slate-400">
              We check the {registerName} register and other official databases.
            </p>
          </form>
        </div>

        {/* Results */}
        {charitySearch.isLoading && (
          <div className="flex items-center justify-center gap-3 py-12">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-violet-500" />
            <p className="text-base text-slate-500">Searching official registries...</p>
          </div>
        )}

        {charitySearch.data && !charitySearch.isLoading && (
          <div className="space-y-4 mb-10">
            {charitySearch.data.results.length === 0 ? (
              <div className="rounded-2xl bg-white p-6 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] sm:p-8">
                <div className="flex items-start gap-3">
                  <FiAlertTriangle className="mt-1 h-5 w-5 shrink-0 text-amber-500" />
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">No results found</h3>
                    <p className="mt-1 text-base text-slate-600">
                      {charitySearch.data.message || `We couldn't find "${searchTerm}" in official charity registers.`}
                    </p>
                    <div className="mt-4 space-y-3">
                      <p className="text-sm font-medium text-slate-700">What this could mean:</p>
                      <ul className="space-y-2 text-sm text-slate-600">
                        <li className="flex gap-2">
                          <span className="text-slate-400 mt-0.5">•</span>
                          <span>The charity might be registered under a different name — try searching with fewer words</span>
                        </li>
                        <li className="flex gap-2">
                          <span className="text-slate-400 mt-0.5">•</span>
                          <span>It might be a very small charity not required to register</span>
                        </li>
                        <li className="flex gap-2">
                          <span className="text-slate-400 mt-0.5">•</span>
                          <span>If someone is pressuring you to donate to this charity, treat it with caution</span>
                        </li>
                      </ul>
                      <p className="text-sm text-slate-500 mt-3">
                        If you're unsure, try searching directly on the{' '}
                        <a
                          href={userMarket === 'US'
                            ? 'https://www.irs.gov/charities-non-profits/tax-exempt-organization-search'
                            : 'https://register-of-charities.charitycommission.gov.uk/charity-search'
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-violet-700 underline underline-offset-2 hover:text-violet-800"
                        >
                          {registerName} website
                        </a>.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-slate-500">
                  {charitySearch.data.results.length} {charitySearch.data.results.length === 1 ? 'result' : 'results'} for "{searchTerm}"
                </p>
                {charitySearch.data.results.map((charity) => (
                  <ResultCard key={`${charity.registrationNumber}-${charity.name}`} charity={charity} />
                ))}
              </>
            )}
          </div>
        )}

        {/* Educational content */}
        <div className="space-y-8 mt-4">

          {/* Red flags */}
          <section className="rounded-2xl bg-white p-6 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] sm:p-8">
            <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl mb-4">
              Red flags to watch for
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                {
                  icon: <FiPhone className="h-5 w-5" />,
                  title: 'High-pressure phone calls',
                  description: 'Legitimate charities won\'t pressure you to donate immediately over the phone or get angry if you want time to think.',
                },
                {
                  icon: <FiMail className="h-5 w-5" />,
                  title: 'Unsolicited emails or texts',
                  description: 'Be cautious of donation requests via email or text, especially after a disaster. Go directly to the charity\'s official website.',
                },
                {
                  icon: <FiDollarSign className="h-5 w-5" />,
                  title: 'Cash or gift card payments',
                  description: 'No legitimate charity will ask you to donate via gift cards, wire transfer, or cryptocurrency. Use a credit card or cheque.',
                },
                {
                  icon: <FiAlertTriangle className="h-5 w-5" />,
                  title: 'Vague about how funds are used',
                  description: 'Real charities can tell you exactly how your donation will be used. If they can\'t or won\'t explain, walk away.',
                },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3 rounded-xl bg-slate-50 p-4">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-500">
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800">{item.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-500">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Safe donating tips */}
          <section className="rounded-2xl bg-white p-6 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] sm:p-8">
            <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl mb-4">
              How to donate safely
            </h2>
            <div className="space-y-4">
              {[
                {
                  step: '1',
                  title: 'Verify the charity is registered',
                  description: `Use the checker above to confirm the charity is on the official ${registerName} register. A registration number is a good sign.`,
                },
                {
                  step: '2',
                  title: 'Go directly to their website',
                  description: 'Don\'t click links in emails or texts. Type the charity\'s web address directly into your browser or search for them.',
                },
                {
                  step: '3',
                  title: 'Pay by card, not cash or gift cards',
                  description: 'Credit and debit cards offer fraud protection. Never donate via gift cards, wire transfer, or cryptocurrency.',
                },
                {
                  step: '4',
                  title: 'Keep a record',
                  description: 'Save confirmation emails and receipts. Legitimate charities will always provide a receipt for your donation.',
                },
                {
                  step: '5',
                  title: 'Take your time',
                  description: 'A real charity will never pressure you to donate on the spot. If you feel rushed, step back and verify before giving.',
                },
              ].map((item) => (
                <div key={item.step} className="flex items-start gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-50 text-sm font-semibold text-violet-700">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-slate-800">{item.title}</h3>
                    <p className="mt-0.5 text-sm leading-relaxed text-slate-500">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Common charity scams */}
          <section className="rounded-2xl bg-white p-6 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] sm:p-8">
            <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl mb-4">
              Common charity scams
            </h2>
            <div className="space-y-4">
              {[
                {
                  title: 'Disaster relief scams',
                  description: 'After earthquakes, floods, or other disasters, scammers set up fake charities with names similar to well-known organisations. They prey on people\'s desire to help. Always donate to established charities you already know.',
                },
                {
                  title: 'Lookalike charities',
                  description: 'Scammers create charities with names very similar to real ones — just a word or two different. "British Heart Association" instead of "British Heart Foundation", for example. Always check the exact name and registration number.',
                },
                {
                  title: 'Door-to-door collectors',
                  description: 'While some legitimate charities do collect door-to-door, scammers also use this approach. Ask for ID, a charity number, and take time to verify before donating. A real collector won\'t mind waiting.',
                },
                {
                  title: 'Social media fundraisers',
                  description: 'Fake fundraisers on social media often use emotional stories and stolen photos. Before donating to a personal fundraiser, verify the person and cause are real. Use established platforms like GoFundMe or JustGiving.',
                },
              ].map((item) => (
                <div key={item.title} className="border-b border-slate-100 pb-4 last:border-b-0 last:pb-0">
                  <h3 className="text-base font-medium text-slate-800">{item.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-slate-500">{item.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Useful links */}
          <section className="rounded-2xl bg-white p-6 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)] sm:p-8">
            <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl mb-4">
              Useful links
            </h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {(userMarket === 'US' ? [
                { label: 'IRS Tax Exempt Organization Search', url: 'https://www.irs.gov/charities-non-profits/tax-exempt-organization-search' },
                { label: 'FTC Charity Scams Guide', url: 'https://consumer.ftc.gov/articles/before-giving-to-charity' },
                { label: 'Charity Navigator', url: 'https://www.charitynavigator.org' },
                { label: 'GuideStar / Candid', url: 'https://www.guidestar.org' },
              ] : [
                { label: 'Charity Commission Register', url: 'https://register-of-charities.charitycommission.gov.uk/charity-search' },
                { label: 'OSCR (Scotland)', url: 'https://www.oscr.org.uk/search' },
                { label: 'Charity Commission NI', url: 'https://www.charitycommissionni.org.uk/charity-search' },
                { label: 'Action Fraud', url: 'https://www.actionfraud.police.uk' },
              ]).map((link) => (
                <a
                  key={link.label}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 rounded-xl bg-slate-50 p-4 transition-colors hover:bg-violet-50 group"
                >
                  <FiExternalLink className="h-4 w-4 shrink-0 text-slate-400 group-hover:text-violet-600" />
                  <span className="text-sm font-medium text-slate-700 group-hover:text-violet-700">{link.label}</span>
                </a>
              ))}
            </div>
          </section>

        </div>
      </div>
    </div>
  )
}
